import {Component, HostListener, Input, OnInit, TemplateRef} from '@angular/core';
import {MemberService} from '../../services/member.service';
import {AlertService} from '../../services/notify/alert.service';
import {ModalService} from '../../services/modal/modal.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {Observable} from 'rxjs';
import {ComponentCanDeactivate} from '../../guards/save-guard';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {MenuModel, SubMenuModel} from '../../models/menu.model';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'app-sidebar-settings',
    templateUrl: './sidebar-settings.component.html',
    styleUrls: ['./sidebar-settings.component.scss']
})
export class SidebarSettingsComponent implements OnInit, ComponentCanDeactivate {
    @Input() visibleHeader = true;

    isShowIcons = false;
    icons = {
        selected: '',
        all: [
            'bi bi-alarm',
            'bi bi-archive',
            'bi bi-at',
            'bi bi-asterisk',
            'bi bi-bar-chart',
            'bi bi-bar-chart-steps',
            'bi bi-bell',
            'bi bi-box-seam',
            'bi bi-bricks',
            'bi bi-briefcase',
            'bi bi-buildings',
            'bi bi-calendar-date',
            'bi bi-camera',
            'bi bi-cash-stack',
            'bi bi-chat-dots',
            'bi bi-check2-all',
            'bi bi-easel',
            'bi bi-pen',
        ],
    };

    showSubItems = false;
    selectedGlobalMenuItem: MenuModel;

    currentMenu: MenuModel[] = [];
    copyCurrentMenu: MenuModel[] = [];
    allSubItems: SubMenuModel[] = [];
    copyAllSubItems: SubMenuModel[] = [];

    currentSubItem: SubMenuModel;

    newGlobalMenuSubItems: SubMenuModel[] = [];


    isMenuEdit = false;

    constructor(
        protected memberService: MemberService,
        protected alert: AlertService,
        protected router: Router,
        public modalService: ModalService,
        public translate: TranslateService,
    ) {
    }

    @HostListener('window: beforeunload', ['$event'])
    deactivate(): boolean {
        if (this.isMenuEdit) {
            return false;
        }
    }

    canDeactivate(): boolean | Observable<boolean> {
        if (this.isMenuEdit) {
            return confirm(this.translate.instant('CONFIRM.TEXT.LEAVE_PAGE'));
        } else {
            return true;
        }
    }

    ngOnInit(): void {
        this.memberService.getCurrentMember().then(res => {
            if (res.project_setting.is_project_admin) {
                this.getMenu();
            } else {
                this.alert.setAlert(this.translate.instant('ALERTS.DENIED'), 'blue');
                void this.router.navigate(['account', 'tasks', 'all']);
            }
        });
    }

    getMenu(): void {
        this.memberService.getMenu(true).subscribe({
            next: res => {
                this.currentMenu = res.project_settings;
                this.copyCurrentMenu = structuredClone(res.project_settings);

                this.allSubItems = res.submenu_items;
                this.copyAllSubItems = structuredClone(res.submenu_items);
            }, error: err => {
                this.alert.setAlert(err, 'blue');
            }
        });
    }

    updateSubItemAfterDrag(e: CdkDragDrop<MenuModel>): void {
        moveItemInArray(this.selectedGlobalMenuItem.children, e.previousIndex, e.currentIndex);
        this.isMenuEdit = true;
    }

    updateGlobalItemAfterDrag(e: CdkDragDrop<MenuModel>): void {
        moveItemInArray(this.currentMenu, e.previousIndex, e.currentIndex);
        this.isMenuEdit = true;
    }

    openGlobalMenuItem(menuItem: MenuModel): void {
        if (menuItem.open) {
            this.showSubItems = false;
            menuItem.open = false;
            this.selectedGlobalMenuItem = null;
        } else {
            if (this.currentMenu.find(x => x.open)) {
                this.currentMenu.find(x => x.open).open = false;
            }
            this.allSubItems = this.copyAllSubItems.filter(subItem => {
                const currentSubItem = menuItem.children.find(currSub => subItem.submenu_key === currSub.submenu_key);
                if (!currentSubItem) {
                    return subItem;
                } else {
                    return;
                }
            });
            menuItem.open = true;
            this.selectedGlobalMenuItem = menuItem;
            this.showSubItems = true;
        }
    }

    openModal(content: TemplateRef<any>, subItem: any): void {
        this.modalService.openTemplateModal(content, 'sm');

        this.currentSubItem = subItem;
    }

    openGlobalMenuModal(content: TemplateRef<any>, globalMenuItem: MenuModel, e: MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();
        this.modalService.openTemplateModal(content);
        this.selectedGlobalMenuItem = globalMenuItem;
    }

    openCreateMenuItemModal(addMenuItem: TemplateRef<any>): void {
        this.allSubItems = structuredClone(this.copyAllSubItems);
        this.selectedGlobalMenuItem = undefined;
        this.newGlobalMenuSubItems = [];
        this.currentMenu.forEach((menuItem: MenuModel) => {
            menuItem.open = false;
        });
        this.modalService.openTemplateModal(addMenuItem);
    }

    addSubItem(permission: 'ALL' | 'ONLY_ADMIN' | 'INNER' | 'BLOCKED', modal: NgbModalRef): void {
        const newChild: SubMenuModel = {
            sub_acc_type: permission,
            submenu_key: this.currentSubItem.submenu_key,
            title_sub: this.currentSubItem.title_sub
        };
        if (this.selectedGlobalMenuItem) {
            const existsSub = this.selectedGlobalMenuItem.children.find(child => child.submenu_key === this.currentSubItem.submenu_key);
            if (existsSub) {
                existsSub.sub_acc_type = permission;
            } else {
                this.allSubItems = this.allSubItems.filter(x => x.submenu_key !== this.currentSubItem.submenu_key);
                this.selectedGlobalMenuItem.children.push(newChild);
            }
            this.isMenuEdit = true;
        } else {
            this.allSubItems = this.allSubItems.filter(x => x.submenu_key !== this.currentSubItem.submenu_key);
            this.newGlobalMenuSubItems.push(newChild);
        }
        modal.close();
    }

    deleteSubMenuItem(subItem: SubMenuModel): void {
        if (this.selectedGlobalMenuItem) {
            this.selectedGlobalMenuItem.children = this.selectedGlobalMenuItem.children.filter(x => x.submenu_key !== subItem.submenu_key);
            this.isMenuEdit = true;
        } else {
            this.newGlobalMenuSubItems = this.newGlobalMenuSubItems.filter(x => x.submenu_key !== subItem.submenu_key);
        }
        this.allSubItems.unshift(subItem);
    }

    deleteMenuItem(menuItem: MenuModel, e: MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();
        const modalSettings: any = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_MENU_ITEM', {title: menuItem.title}),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const confirmModal = this.modalService.createFromSettings(modalSettings);
        confirmModal.result.then(data => {
            if (data) {
                this.currentMenu = this.currentMenu.filter(x => JSON.stringify(x) !== JSON.stringify(menuItem));
                this.isMenuEdit = true;
            }
        }).catch(() => {
            return;
        });
    }

    createGlobalMenuItem(name: string, modal: NgbModalRef): void {
        if (!name) {
            return this.alert.setAlert(this.translate.instant('ALERTS.SET_NAME'), 'blue');
        }
        if (!this.icons.selected) {
            return this.alert.setAlert(this.translate.instant('ALERTS.SET_ICON'), 'blue');
        }
        const similarGlobalMenu = this.currentMenu.find(item => {
            if ((item.title + item.icon) === (name + this.icons.selected)) {
                return item;
            }
        });
        if (similarGlobalMenu) {
            return this.alert.setAlert(this.translate.instant('ALERTS.MENU_ERROR'), 'blue');
        }
        const data: MenuModel = {
            title: name,
            icon: this.icons.selected,
            children: this.newGlobalMenuSubItems,
            open: false,
        };

        this.currentMenu.push(data);
        this.isMenuEdit = true;
        modal.close();
    }

    saveMenu(): void {
        const copyNewMenu = structuredClone(this.currentMenu);
        const newMenu = copyNewMenu.map(item => {
            delete item.open;
            delete item.acc_type;
            item.children = item.children.map(child => {
                delete child.title_sub;
                return child;
            });

            return item;
        });

        this.memberService.saveMenu(JSON.stringify(newMenu)).subscribe({
            next: res => {
                this.isMenuEdit = false;
                this.getMenu();
                this.memberService.refreshMenuEvent.emit(res.project_settings);
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    cancelSaveMenu(): void {
        this.currentMenu = structuredClone(this.copyCurrentMenu);
        this.isMenuEdit = false;
        this.showSubItems = false;
    }

    previousDefaultMenu(): void {
        const modalSettings: any = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.ATTENTION'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DEFAULT_MENU'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const confirmModal = this.modalService.createFromSettings(modalSettings);
        confirmModal.result.then(data => {
            if (data) {
                this.currentMenu = [];
                this.saveMenu();
            }
        });
    }
}
